// =============================================================================
// src/features/crisis/crisis-ticker-feed.ts
// Conteudo e tipos do ticker ao vivo (manchetes + pilulas de metrica).
//
// Proveniencia: porte FIEL de assets/crisis.js 199-226 (tNews/tMetric +
//   tickerCrisis/tickerCalm). Separado do renderer/animacao para isolar o
//   conteudo de demonstracao (fonte unica, greppavel por estado).
//
// SRP: so dados. Nenhuma chamada de DOM nem timing aqui.
// =============================================================================

/** Estado de demonstracao que seleciona o feed exibido (assets/crisis.js 256). */
export type CrisisTickerView = 'crisis' | 'calm';

/** Polaridade de sentimento de uma manchete (assets/crisis.js 199). */
export type TickerSentiment = 'neg' | 'neu' | 'pos';

/** Direcao da pilula de metrica (assets/crisis.js 238-240). */
export type TickerMetricDir = 'up-bad' | 'up-good' | 'down-bad' | 'flat' | '';

/** Item de manchete do feed (assets/crisis.js 199-201). */
export interface TickerNewsItem {
  readonly kind: 'news';
  readonly src: string;
  readonly text: string;
  readonly sent: TickerSentiment;
  readonly time: string;
}

/** Item de metrica do feed (assets/crisis.js 202-204). */
export interface TickerMetricItem {
  readonly kind: 'metric';
  readonly label: string;
  readonly value: string;
  readonly dir: TickerMetricDir;
}

/** Item do ticker: manchete ou metrica. */
export type TickerItem = TickerNewsItem | TickerMetricItem;

/** Constroi um item de manchete (assets/crisis.js 199-201). */
function tickerNews(src: string, text: string, sent: TickerSentiment, time: string): TickerNewsItem {
  return { kind: 'news', src, text, sent, time };
}

/** Constroi um item de metrica (assets/crisis.js 202-204). */
function tickerMetric(label: string, value: string, dir: TickerMetricDir = ''): TickerMetricItem {
  return { kind: 'metric', label, value, dir };
}

/** Feed do estado de crise (assets/crisis.js 206-216). */
const TICKER_CRISIS: readonly TickerItem[] = [
  tickerNews('Portal Amazonas Agora', 'contrato do pronto-socorro da Zona Leste sob questionamento', 'neg', '07h12'),
  tickerMetric('Menções 24h', '+180%', 'up-bad'),
  tickerNews('@oposicao_local', '“cadê a explicação sobre o valor da obra do pronto-socorro?”', 'neg', '08h40'),
  tickerMetric('Sentimento', '−38', 'down-bad'),
  tickerNews('Rádio Cidade FM', 'cita edital público da reforma no boletim da manhã', 'neu', '08h05'),
  tickerMetric('Alcance', '320 mil', ''),
  tickerNews('Parlamentar adversário', 'anuncia pedido de informação no Senado sobre a obra', 'neg', '09h18'),
  tickerNews('Liderança aliada', 'defende transparência e cita o Portal da Transparência do mandato', 'pos', '09h05'),
  tickerMetric('Aceleração', 'subindo', 'up-bad'),
];

/** Feed do estado calmo (assets/crisis.js 218-226). */
const TICKER_CALM: readonly TickerItem[] = [
  tickerNews('Monitoramento', 'nenhum tema ultrapassou o limite de atenção nas últimas 24h', 'pos', 'agora'),
  tickerMetric('Menções 24h', '412 · estável', 'flat'),
  tickerNews('Agenda Zona Leste', 'repercute bem em veículos e perfis locais', 'pos', '11h20'),
  tickerMetric('Sentimento', '+11', 'up-good'),
  tickerNews('Projeto de mobilidade', 'citado em portal regional sem controvérsia', 'neu', '10h02'),
  tickerMetric('Maior pico', 'agenda na Zona Leste', ''),
  tickerNews('Repasse à educação', 'pauta acompanhada, volume baixo e estável', 'neu', '09h48'),
];

/** Devolve o feed do estado pedido (assets/crisis.js 256). */
export function tickerFeedFor(view: CrisisTickerView): readonly TickerItem[] {
  return view === 'calm' ? TICKER_CALM : TICKER_CRISIS;
}
